<template>
  <VueQueryDevtools />
  <router-view></router-view>
</template>

<script setup>
import {
  getCurrentInstance,
  provide,
  computed,
  watch,
  ref,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { VueQueryDevtools } from "@tanstack/vue-query-devtools";
import { useRoute } from "vue-router";
import { storeDispatcher } from "./shared/utils";
import { useHooks } from "./shared/hooks";

const { useState } = useHooks();

const VERSION_CHECK_INTERVAL_MS = 60000;

const interval = ref(null);
const [loadedVersion, setLoadedVersion] = useState(null);
const [loadedLastUpdate, setLoadedLastUpdate] = useState(null);

async function fetchMeta() {
  const response = await fetch(`/meta.json?t=${Date.now()}`, {
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(`meta.json fetch failed: ${response.status}`);
  }
  return response.json();
}

async function checkForUpdates() {
  try {
    const data = await fetchMeta();
    const hasNewVersion =
      data.version !== loadedVersion.value ||
      data.lastUpdate !== loadedLastUpdate.value;

    if (hasNewVersion) {
      storeDispatcher("isNewUpdateVersion", true);
    }
  } catch {
    // silently ignore — version check is non-critical
  }
}

async function initializeVersionCheck() {
  const data = await fetchMeta();
  setLoadedVersion(data.version);
  setLoadedLastUpdate(data.lastUpdate);
}

const handleVisibilityChange = () => {
  if (document.visibilityState === "visible") {
    checkForUpdates();
  }
};

onMounted(async () => {
  if (import.meta.env.PROD) {
    await initializeVersionCheck();
    interval.value = setInterval(checkForUpdates, VERSION_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibilityChange);
  }
});
onBeforeUnmount(() => {
  clearInterval(interval.value);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
});

const vm = getCurrentInstance().appContext.config.globalProperties;
provide(
  "vm",
  computed(() => vm)
);
const route = useRoute();
watch(
  route,
  (val) => {
    const isActiveMatomo = import.meta.env.VUE_APP_MATOMO_ACTIVE === "true";
    if (val && isActiveMatomo) {
      try {
        const title = window.document.title;
        window._paq.push(["setDocumentTitle", title]);
        window._paq.push(["trackPageView"]);
      } catch (e) {}
    }
  },
  { immediate: true }
);
</script>

<style lang="scss">
@import "@/assets/scss/re-design/style.scss";
</style>
